/**
 * Convert a project's `repo_url` (either SSH or HTTPS git URL) into
 * a web-browsable base URL, plus helpers for compare-branch links.
 *
 * We accept three input forms:
 *   git@github.com:owner/repo.git           → https://github.com/owner/repo
 *   https://github.com/owner/repo.git       → https://github.com/owner/repo
 *   https://github.com/owner/repo           → https://github.com/owner/repo
 *
 * Returns `null` when the URL isn't a recognised GitHub URL — caller
 * is expected to hide the link affordance in that case rather than
 * render a broken anchor. (Self-hosted GitLab / Bitbucket are not yet
 * supported; could be added later by sniffing host and adjusting the
 * compare path.)
 */
export function gitRepoWebUrl(repoUrl: string | null | undefined): string | null {
  if (!repoUrl) return null;
  const trimmed = repoUrl.trim();
  if (!trimmed) return null;

  // SSH: git@github.com:owner/repo.git
  const sshMatch = trimmed.match(/^git@([^:]+):([^\s]+?)(?:\.git)?$/);
  if (sshMatch) {
    const host = sshMatch[1] ?? '';
    const path = sshMatch[2] ?? '';
    if (!host || !path) return null;
    if (host === 'github.com') return `https://github.com/${path}`;
    return null;
  }

  // HTTPS: strip trailing .git and any fragment/query.
  const httpsMatch = trimmed.match(/^https?:\/\/([^/]+)\/(.+?)(?:\.git)?\/?$/);
  if (httpsMatch) {
    const host = httpsMatch[1] ?? '';
    const path = httpsMatch[2] ?? '';
    if (!host || !path) return null;
    if (host === 'github.com' || host.endsWith('.github.com')) {
      return `https://github.com/${path}`;
    }
    return null;
  }

  return null;
}

/**
 * Build a GitHub compare URL for `<base>...<head>`. Used to make
 * branch names clickable from the task UI so a reviewer can jump
 * straight to the diff without leaving for the terminal.
 *
 * Caller is responsible for handling `null` (no usable repo URL).
 */
/**
 * Defensive cleanup for the `branch` field. Older task rows have ANSI
 * escape sequences (e.g. `\x1b[22m` from `pc.dim(...)`) baked in from
 * before the panel-worker started stripping them at capture time —
 * concatenating those directly into a URL produces a broken compare
 * link. We strip ANSI here AND drop any character git itself would
 * reject in a branch name (space, control, `~`, `^`, `:`, `?`, `*`,
 * `[`, backslash) so even other accidental contamination doesn't leak
 * into the href.
 */
export function cleanBranchName(branch: string): string {
  return branch
    // eslint-disable-next-line no-control-regex
    .replace(/\x1b\[[0-9;?]*[A-Za-z]/g, '')
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x1f\x7f\s~^:?*[\\]/g, '')
    .trim();
}

export function gitHubCompareUrl(
  repoUrl: string | null | undefined,
  baseBranch: string,
  headBranch: string,
): string | null {
  const web = gitRepoWebUrl(repoUrl);
  if (!web) return null;
  const cleanBase = cleanBranchName(baseBranch);
  const cleanHead = cleanBranchName(headBranch);
  if (!cleanBase || !cleanHead) return null;
  // Branch names are URL-safe enough that encodeURIComponent would only
  // hurt readability (`/` → `%2F`). Git itself rejects truly unsafe
  // characters in branch names, so a verbatim path matches what users
  // copy/paste from git output and matches GitHub's own URL shape.
  return `${web}/compare/${cleanBase}...${cleanHead}`;
}
